import {FormikValues} from 'formik'
import {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import * as Yup from 'yup'
import {
  BooleanApiData,
  EInvoiceParameterMaskedDto,
  EInvoiceParameterMaskedDtoApiData,
  EInvoiceParameterUpsertRequest,
} from '../../../api/data-contracts'
import {
  Api_Admin_CreateEInvoiceParameter,
  Api_Admin_DeleteEInvoiceParameter,
  Api_Admin_GetEInvoiceParameter,
  Api_Admin_UpdateEInvoiceParameter,
} from '../../../apiClient/apiClient'
import FormMessage from '../../../components/common/messages/FormMessage'
import FormikForm from '../../../components/form/FormikForm'
import FormInput from '../../../components/form/FormInput'
import {getRequestAsync, postRequestAsync} from '../../../redux/commonSlice'
import {FormStatus} from '../../../types/FormStatus'

const FIELDS: {name: keyof EInvoiceParameterUpsertRequest; label: string; required?: boolean}[] = [
  {name: 'foribaUserName', label: 'Foriba Kullanıcı Adı', required: true},
  {name: 'foribaPassword', label: 'Foriba Şifresi', required: true},
  {name: 'einvoiceIdentifier', label: 'E-Fatura Etiketi', required: true},
  {name: 'vknTckn', label: 'VKN/TCKN', required: true},
  {name: 'defaultQuantityCode', label: 'Varsayılan Miktar Kodu', required: true},
  {name: 'defaultScenario', label: 'Varsayılan Senaryo', required: true},
  {name: 'infoEmail', label: 'Bilgi E-postası', required: true},
  {name: 'phoneNumber', label: 'Telefon', required: true},
  {name: 'taxAdministration', label: 'Vergi Dairesi', required: true},
  {name: 'foribaSupplierFirstName', label: 'Foriba Tedarikçi Adı', required: true},
  {name: 'foribaSupplierLastName', label: 'Foriba Tedarikçi Soyadı', required: true},
]

function EInvoiceParameterSection() {
  const dispatch = useDispatch()
  const [existing, setExisting] = useState<EInvoiceParameterMaskedDto | null>(null)
  const [loaded, setLoaded] = useState(false)

  const fetchExisting = async () => {
    await dispatch<any>(
      getRequestAsync({
        getRequestFunction: Api_Admin_GetEInvoiceParameter,
      })
    )
      .unwrap()
      .then(async (response: EInvoiceParameterMaskedDtoApiData) => {
        setExisting(response.data ?? null)
        setLoaded(true)
      })
  }

  useEffect(() => {
    fetchExisting()
  }, [])

  const emptyValues: EInvoiceParameterUpsertRequest = FIELDS.reduce((acc, f) => {
    acc[f.name] = ''
    return acc
  }, {} as EInvoiceParameterUpsertRequest)

  const isMasked = (value?: string | null) => !!value && value.includes('*')

  const initialFormValues: EInvoiceParameterUpsertRequest = existing
    ? FIELDS.reduce((acc, f) => {
        const val = (existing as any)[f.name] as string | null | undefined
        acc[f.name] = f.name === 'foribaPassword' || isMasked(val) ? '' : val ?? ''
        return acc
      }, {} as EInvoiceParameterUpsertRequest)
    : emptyValues

  const submit = async (values: EInvoiceParameterUpsertRequest, actions: FormikValues) => {
    actions.setStatus()
    await dispatch<any>(
      postRequestAsync({
        postRequestFunction: existing
          ? Api_Admin_UpdateEInvoiceParameter
          : Api_Admin_CreateEInvoiceParameter,
        body: values,
      })
    )
      .unwrap()
      .then(async (response: EInvoiceParameterMaskedDtoApiData) => {
        if (response.isSuccessful) {
          setExisting(response.data ?? null)
          actions.setStatus(new FormStatus(true, 'E-Fatura ayarları kaydedildi.'))
        } else {
          actions.setStatus(new FormStatus(false, response.serverMessage))
        }
      })
  }

  const handleDelete = async () => {
    if (!window.confirm('E-Fatura ayarları silinsin mi?')) return
    await dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_Admin_DeleteEInvoiceParameter,
        body: {},
      })
    )
      .unwrap()
      .then(async (response: BooleanApiData) => {
        if (response.data === true) {
          setExisting(null)
        }
      })
  }

  const schema = Yup.object(
    FIELDS.reduce((acc, f) => {
      if (f.required) acc[f.name] = Yup.string().required('Zorunlu alan')
      return acc
    }, {} as Record<string, Yup.AnySchema>)
  )

  if (!loaded) return null

  return (
    <>
      {existing && (
        <div className='card card-dashed p-6 mb-5'>
          {FIELDS.map((f) => (
            <div className='row mb-2' key={f.name}>
              <div className='col-xl-3 fw-bold'>{f.label}</div>
              <div className='col-xl-9'>{(existing as any)[f.name]}</div>
            </div>
          ))}
          <div className='row mt-3'>
            <div className='col-xl-12 text-end'>
              <button type='button' className='btn btn-danger btn-sm' onClick={handleDelete}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      <FormikForm
        initialValues={initialFormValues}
        onSubmit={submit}
        validationSchema={schema}
        enableReinitialize={true}
      >
        {(form_props) => (
          <>
            <div className='row'>
              {FIELDS.map((f) => (
                <div className='col-xl-6 mb-5' key={f.name}>
                  <FormInput
                    fieldAttributes={{
                      type: f.name === 'foribaPassword' ? 'password' : 'text',
                      name: f.name,
                      placeholder: f.label,
                    }}
                    istouched={(form_props.touched as any)[f.name] ?? false}
                    errorMessage={(form_props.errors as any)[f.name]}
                    placeHolderOnTop={true}
                    isRequired={f.required}
                    checkValid={true}
                  />
                </div>
              ))}
            </div>
            <div className='row mb-5'>
              <div className='col-xl-12 text-end'>
                <button type='submit' className='btn btn-primary'>
                  {existing ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </div>
            <div className='row'>
              <div className='col-xl-12'>
                <FormMessage {...form_props.status} />
              </div>
            </div>
          </>
        )}
      </FormikForm>
    </>
  )
}

export default EInvoiceParameterSection
